import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { openKlaviyoBackInStockModal } from '@theme/product-form';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #variantExplicitlySelected = false;

  connectedCallback() {
    super.connectedCallback();

    // Match buy-buttons Liquid: product.selected_variant is set when URL has ?variant=
    // (or merchant selected a variant). Without this, sticky ATC stays on "Select a size".
    this.#variantExplicitlySelected =
      this.dataset.variantExplicitlySelected === 'true' ||
      Boolean(new URLSearchParams(window.location.search).get('variant'));

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.#updateButtonText();
    // Main ATC may apply Notify Me after connect / Klaviyo trigger injection
    requestAnimationFrame(() => this.#updateButtonText());
    setTimeout(() => this.#updateButtonText(), 100);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // On desktop the product details column is sticky, so buy buttons never scroll
    // out of view. Use the media/gallery column instead as the scroll trigger.
    const isDesktop = window.innerWidth >= 750;
    const isDstrezzed = this.dataset.dstrezzedMode === 'true';
    const mediaColumn = isDstrezzed && isDesktop
      ? this.closest('.shopify-section')?.querySelector('.product-information__media')
      : null;
    const scrollTarget = mediaColumn ?? buyButtonsBlock;

    // Observer for scroll trigger visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        // Only show when scrolled past (above viewport)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = scrollTarget.getBoundingClientRect();
          // Only show if scroll target is above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(scrollTarget);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar.
   * Matches main ATC: Notify Me opens Klaviyo BIS; otherwise puppet-clicks main ATC
   * (which has data-add-to-cart-animation="false").
   */
  handleAddToCartClick = () => {
    const isDstrezzed = this.dataset.dstrezzedMode === 'true';

    if (isDstrezzed) {
      const variantPicker = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
      if (variantPicker) {
        variantPicker.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    // Apply Klaviyo Notify Me / ATC / Sold out using the same rules as product-form
    this.#updateButtonText(variant);
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (_event) => {
    // VariantSelectedEvent only carries optionValueId; variant id arrives on variant:update.
    this.#variantExplicitlySelected = true;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.#updateButtonText();
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity.
   * Dstrezzed sticky uses the same Klaviyo Notify Me rules as main ATC (product-form).
   * @param {{ available?: boolean, id?: string|number } | null} [variant]
   */
  #updateButtonText(variant = null) {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    if (this.dataset.dstrezzedMode === 'true') {
      addToCartButton.dataset.selectSize = 'true';
      addToCartButton.disabled = false;
      delete addToCartButton.dataset.notifyMe;
      const textSpan = addToCartButton.querySelector('.add-to-cart-text');
      if (textSpan) {
        const textNode = [...textSpan.childNodes].find((n) => n.nodeType === Node.TEXT_NODE);
        if (textNode) textNode.textContent = this.dataset.selectSizeText || 'Select a size';
      }
      quantityDisplay.style.display = 'none';
      return;
    }

    delete addToCartButton.dataset.selectSize;

    if (this.dataset.dstrezzedMode === 'true') {
      this.#syncNotifyMeButtonText(variant);
    } else {
      const available = !addToCartButton.disabled;
      const addToCartLabel = this.dataset.addToCartText || 'Add to cart';
      const soldOutLabel = this.dataset.soldOutText || 'Sold out';
      const textSpan = addToCartButton.querySelector('.add-to-cart-text');
      if (textSpan) {
        const textNode = [...textSpan.childNodes].find((n) => n.nodeType === Node.TEXT_NODE);
        if (textNode) {
          const label = textNode.textContent.trim();
          if (!available && (label === 'Select a size' || label === addToCartLabel)) {
            textNode.textContent = soldOutLabel;
          } else if (available && (label === 'Select a size' || label === soldOutLabel)) {
            textNode.textContent = addToCartLabel;
          }
        }
      }
    }

    const showQuantity =
      !addToCartButton.disabled &&
      addToCartButton.dataset.notifyMe !== 'true' &&
      this.#currentQuantity > 1;

    quantityNumber.textContent = this.#currentQuantity.toString();
    quantityDisplay.style.display = showQuantity ? 'inline' : 'none';
  }

  /**
   * Same Notify Me rules as product-form `#syncNotifyMeButtonText`:
   * sold-out + Klaviyo BIS enabled + `.klaviyo-bis-trigger` present → Notify Me.
   * @param {{ available?: boolean, id?: string|number } | null} [variant]
   * @returns {boolean}
   */
  #syncNotifyMeButtonText(variant = null) {
    const { addToCartButton } = this.refs;
    const addToCartLabel = this.dataset.addToCartText || 'Add to cart';
    const soldOutLabel = this.dataset.soldOutText || 'Sold out';
    const textSpan = addToCartButton.querySelector('.add-to-cart-text');
    const textNode = textSpan && [...textSpan.childNodes].find((n) => n.nodeType === Node.TEXT_NODE);

    const variantPicker = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);

    if (!(variantPicker instanceof HTMLElement)) {
      delete addToCartButton.dataset.notifyMe;
      return false;
    }

    const notifyEnabled = variantPicker.dataset.notifySoldOutEnabled === 'true';
    const klaviyoReady = Boolean(document.querySelector('.klaviyo-bis-trigger'));
    const notifyLabel = variantPicker.dataset.notifySoldOutLabel || 'Notify Me When Available';

    let isSoldOutSelection = false;
    if (variant && typeof variant === 'object') {
      isSoldOutSelection = variant.available === false;
      if (variant.id) this.dataset.currentVariantId = String(variant.id);
    } else if (this.dataset.variantAvailable === 'false') {
      isSoldOutSelection = true;
    } else {
      const selectedInput = /** @type {HTMLInputElement | null} */ (
        variantPicker.querySelector('fieldset input:checked')
      );
      isSoldOutSelection = selectedInput?.dataset.variantOutOfStock === 'true';
    }

    if (notifyEnabled && isSoldOutSelection && klaviyoReady) {
      addToCartButton.dataset.notifyMe = 'true';
      addToCartButton.disabled = false;
      if (textNode) textNode.textContent = notifyLabel;
      return true;
    }

    delete addToCartButton.dataset.notifyMe;

    if (isSoldOutSelection) {
      addToCartButton.disabled = true;
      if (textNode) textNode.textContent = soldOutLabel;
    } else {
      addToCartButton.disabled = false;
      if (textNode && textNode.textContent.trim() !== addToCartLabel) {
        const current = textNode.textContent.trim();
        if (current === 'Select a size' || current === soldOutLabel || current === notifyLabel) {
          textNode.textContent = addToCartLabel;
        }
      }
    }

    return false;
  }

  /**
   * Resolves the variant id for Klaviyo BIS (URL → sticky dataset → form hidden input).
   * @returns {string | undefined}
   */
  #getSelectedVariantId() {
    const fromUrl = new URLSearchParams(window.location.search).get('variant');
    if (fromUrl) return fromUrl;

    if (this.dataset.currentVariantId) return this.dataset.currentVariantId;

    const formInput = this.#getProductForm()?.querySelector('input[name="id"]');
    return formInput instanceof HTMLInputElement ? formInput.value : undefined;
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
